import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Download, Loader2, Mail, MailX, Search, Undo2, UserCheck,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  exportNewsletterSubscribers,
  getNewsletterSubscribers,
  updateNewsletterSubscriber,
} from '@/lib/api';
import {
  SUBSCRIBER_STATUSES,
  displayValue,
  formatDate,
  formatDateTime,
  localeLabel,
  sourcePageLabel,
  subscriberStatusMeta,
} from '@/lib/websiteEngagement';
import WebsiteEngagementShell from '@/components/admin/WebsiteEngagementShell';
import PortalEmailComposer from '@/components/admin/PortalEmailComposer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DesktopTableOnly,
  MobileCard,
  MobileCardField,
  MobileCardFields,
  MobileCardHeader,
  MobileCardList,
  MobileCardNotice,
  ROW_DENSITY_CLASSES,
  StatusPill,
  TableEmptyRow,
  TableFilterCard,
  adminInputClass,
  filterPillActiveClass,
  filterPillClass,
  tableCardClass,
  tableCellClass,
  tableHeadCellClass,
  tableHeaderRowClass,
  tableRowClass,
} from '@/components/admin/AdminTableStyles';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 25;
const ALL = 'all'; // sentinel for "no filter" (Radix Select cannot use "")

const COLUMNS = [
  { id: 'select', label: '', min: 44, grow: 0, fixed: true },
  { id: 'email', label: 'Email', min: 220, grow: 2 },
  { id: 'status', label: 'Status', min: 120, grow: 0.7 },
  { id: 'locale', label: 'Language', min: 110, grow: 0.7 },
  { id: 'source', label: 'Signed up on', min: 180, grow: 1.4 },
  { id: 'consent', label: 'Consent', min: 150, grow: 1 },
  { id: 'subscribed', label: 'Subscribed', min: 110, grow: 0.8 },
  { id: 'actions', label: 'Actions', min: 130, grow: 0, fixed: true },
];

const GRID_TEMPLATE_COLUMNS = COLUMNS
  .map((column) => (column.fixed ? `${column.min}px` : `minmax(${column.min}px, ${column.grow}fr)`))
  .join(' ');
const TABLE_MIN_WIDTH = COLUMNS.reduce((total, column) => total + column.min, 0);
const ROW_PADDING = ROW_DENSITY_CLASSES.comfortable;
const SKELETON_ROWS = 5;
const STICKY_LEFT = 'sticky left-0 z-10 bg-white group-hover:bg-[#F7F8FA]';
const STICKY_RIGHT = 'sticky right-0 z-10 bg-white group-hover:bg-[#F7F8FA]';
const STICKY_HEAD_LEFT = 'sticky left-0 z-20 bg-[#062B1A]';
const STICKY_HEAD_RIGHT = 'sticky right-0 z-20 bg-[#062B1A]';

const EMPTY_SUMMARY = { total: 0, active: 0, unsubscribed: 0 };

function SubscriberStatCard({ label, value, icon: Icon, tone, accent = false }) {
  return (
    <Card className={cn('border border-[rgba(6,43,26,0.08)] shadow-sm', accent && 'gold-border-left')}>
      {/* Two-up and tighter on a phone. At full width each of these is ~250px
          tall, so a row of them becomes a wall to scroll past before reaching
          the records they describe — seven of them on the signups list. */}
      <CardContent className="p-3 sm:p-4">
        <div className="mb-2 flex items-center justify-between gap-2 sm:mb-3 sm:gap-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/50 sm:text-xs sm:tracking-[0.1em]">
            {label}
          </span>
          <span className={cn('rounded-full p-1 sm:p-1.5', tone)}>
            <Icon className="size-3.5 sm:size-4" />
          </span>
        </div>
        <p
          className="text-2xl font-bold text-[#062B1A] sm:text-3xl"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Confirming a status change by hand.
 *
 * Both directions are worth stopping for, and for opposite reasons: removing
 * somebody is what they asked for and must not happen by a stray click, and
 * putting somebody back means the college is asserting a consent the person
 * did not give in that moment — so the dialog says so out loud.
 */
function SubscriberStatusDialog({ open, subscriber, nextStatus, busy, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  const unsubscribing = nextStatus === 'unsubscribed';

  useEffect(() => { if (open) setReason(''); }, [open]);

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && !next && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {unsubscribing ? 'Unsubscribe this address?' : 'Put this address back on the list?'}
          </DialogTitle>
          <DialogDescription>
            <span className="block text-[#062B1A]">{subscriber?.email}</span>
            <span className="mt-1 block">
              {unsubscribing
                ? 'They will stop receiving the newsletter. The record and its consent history stay, so they can subscribe again themselves.'
                : 'Fresh consent evidence is recorded against your name, so anyone reading this record later can see the college reactivated it rather than the person opting in again.'}
            </span>
          </DialogDescription>
        </DialogHeader>
        {unsubscribing && (
          <div className="space-y-1.5">
            <Label htmlFor="unsubscribe-reason">Reason (optional)</Label>
            <Input
              id="unsubscribe-reason"
              value={reason}
              disabled={busy}
              placeholder="Asked us by phone, bounced, …"
              onChange={(event) => setReason(event.target.value)}
              className={adminInputClass}
            />
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={busy}
            onClick={() => onConfirm({ status: nextStatus, reason: reason.trim() })}
            className={unsubscribing
              ? 'bg-red-700 text-white hover:bg-red-800'
              : 'bg-[#062B1A] text-white hover:bg-[#041B10]'}
          >
            {busy
              ? <Loader2 className="mr-2 size-4 animate-spin" />
              : unsubscribing
                ? <MailX className="mr-2 size-4" />
                : <Undo2 className="mr-2 size-4" />}
            {unsubscribing ? 'Unsubscribe' : 'Reactivate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminNewsletterSubscribers() {
  const { loading: authLoading } = useAuth();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [sources, setSources] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [permissionHints, setPermissionHints] = useState({});
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState(ALL);
  const [localeFilter, setLocaleFilter] = useState(ALL);
  const [sourceFilter, setSourceFilter] = useState(ALL);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);

  const [selectedIds, setSelectedIds] = useState([]);
  const [statusTarget, setStatusTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const canExport = permissions['newsletter.export'] === true;
  const canEdit = permissions['newsletter.edit'] !== false;
  const exportHint = permissionHints['newsletter.export']
    || 'Only a full administrator can export subscriber addresses.';

  useEffect(() => {
    const id = window.setTimeout(() => setQ(searchInput.trim()), 350);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, localeFilter, sourceFilter, dateFrom, dateTo, q]);

  const filterParams = useCallback(() => {
    const params = {};
    if (statusFilter !== ALL) params.status = statusFilter;
    if (localeFilter !== ALL) params.locale = localeFilter;
    if (sourceFilter !== ALL) params.source = sourceFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (q) params.q = q;
    return params;
  }, [statusFilter, localeFilter, sourceFilter, dateFrom, dateTo, q]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getNewsletterSubscribers({
        ...filterParams(), skip: page * PAGE_SIZE, limit: PAGE_SIZE,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total) || 0);
      setSummary({ ...EMPTY_SUMMARY, ...(data?.summary || {}) });
      setSources(Array.isArray(data?.sources) ? data.sources : []);
      setPermissions(data?.permissions || {});
      setPermissionHints(data?.permission_hints || {});
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not load newsletter subscribers');
      setItems([]);
      setTotal(0);
      setSummary(EMPTY_SUMMARY);
    } finally {
      setLoading(false);
    }
  }, [filterParams, page]);

  useEffect(() => {
    if (authLoading) return;
    load();
  }, [authLoading, load]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, page * PAGE_SIZE + items.length);
  const hasActiveFilters = statusFilter !== ALL || localeFilter !== ALL
    || sourceFilter !== ALL || !!dateFrom || !!dateTo || !!q;

  const headerCount = useMemo(() => {
    if (loading) return 'Loading…';
    return `${rangeStart}–${rangeEnd} of ${total} subscriber${total === 1 ? '' : 's'}`;
  }, [loading, rangeStart, rangeEnd, total]);

  const clearFilters = () => {
    setStatusFilter(ALL);
    setLocaleFilter(ALL);
    setSourceFilter(ALL);
    setDateFrom('');
    setDateTo('');
    setSearchInput('');
    setQ('');
  };

  const visibleIds = items.map((item) => item.id);
  const allVisibleSelected = visibleIds.length > 0
    && visibleIds.every((id) => selectedIds.includes(id));
  const toggleRow = (id) => setSelectedIds((current) => (
    current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
  ));

  // Only an active subscriber may be emailed, so a selection that includes an
  // unsubscribed row simply does not offer them — rather than offering them and
  // having the server drop them silently.
  const mailable = items.filter(
    (item) => selectedIds.includes(item.id) && item.status === 'active' && item.consent_to_marketing,
  );
  const excludedFromMail = selectedIds.length - mailable.length;

  const runStatusChange = async (payload) => {
    const target = statusTarget?.subscriber;
    if (!target) return;
    try {
      setBusy(true);
      await updateNewsletterSubscriber(target.id, payload);
      toast.success(payload.status === 'unsubscribed'
        ? `${target.email} unsubscribed`
        : `${target.email} reactivated`);
      setStatusTarget(null);
      await load();
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not update this subscriber');
    } finally {
      setBusy(false);
    }
  };

  const runExport = async () => {
    try {
      setExporting(true);
      const { data } = await exportNewsletterSubscribers(filterParams());
      const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'newsletter-subscribers.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not export the subscriber list');
    } finally {
      setExporting(false);
    }
  };

  return (
    <WebsiteEngagementShell
      title="Newsletter Subscribers"
      description={
        'Everyone who opted into the newsletter on the public website, and everyone who has '
        + 'since opted out. Every message sent from here carries that subscriber’s own '
        + 'unsubscribe link.'
      }
      actions={(
        <>
          <PortalEmailComposer
            audience="newsletter_subscribers"
            recipients={mailable.map((item) => ({
              id: item.id, label: item.email, email: item.email,
            }))}
            buttonLabel={`Email ${mailable.length || ''}`.trim()}
            disabled={mailable.length === 0}
            onSent={load}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="export-subscribers"
            disabled={!canExport || exporting || loading}
            title={canExport ? undefined : exportHint}
            onClick={runExport}
            className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
          >
            {exporting
              ? <Loader2 className="mr-2 size-4 animate-spin" />
              : <Download className="mr-2 size-4" />}
            Export CSV
          </Button>
        </>
      )}
    >
      <section
        aria-label="Newsletter summary"
        className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4"
        data-testid="subscriber-summary"
      >
        <SubscriberStatCard label="Active" value={summary.active} icon={UserCheck} tone="bg-emerald-50 text-emerald-700" accent />
        <SubscriberStatCard label="Unsubscribed" value={summary.unsubscribed} icon={MailX} tone="bg-slate-50 text-slate-600" />
        <div className="col-span-2 md:col-span-1">
          <SubscriberStatCard label="Total on record" value={summary.total} icon={Mail} tone="bg-[#062B1A]/5 text-[#062B1A]" />
        </div>
      </section>

      <TableFilterCard>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="relative max-w-xl flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#062B1A]/40" />
            <Input
              type="search"
              aria-label="Search newsletter subscribers"
              placeholder="Search by email address..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className={cn('pl-9', adminInputClass)}
            />
          </div>
          <div className="text-xs text-[#062B1A]/60">{headerCount}</div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={statusFilter === ALL}
            onClick={() => setStatusFilter(ALL)}
            className={cn(filterPillClass, statusFilter === ALL && filterPillActiveClass)}
          >
            All
          </Button>
          {SUBSCRIBER_STATUSES.map((status) => (
            <Button
              key={status}
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={statusFilter === status}
              onClick={() => setStatusFilter(status)}
              className={cn(filterPillClass, statusFilter === status && filterPillActiveClass)}
            >
              {subscriberStatusMeta(status).label}
            </Button>
          ))}

          <Select value={localeFilter} onValueChange={setLocaleFilter}>
            <SelectTrigger aria-label="Language filter" className={cn('h-8 w-full sm:w-[10rem]', adminInputClass)}>
              <SelectValue placeholder="Language" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Any language</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="fr">Français</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger aria-label="Source filter" className={cn('h-8 w-full sm:w-[15rem]', adminInputClass)}>
              <SelectValue placeholder="Source page" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Any source page</SelectItem>
              {sources.map((source) => (
                <SelectItem key={source} value={source}>{sourcePageLabel(source)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <label className="flex flex-1 items-center gap-1.5 text-xs text-[#062B1A]/60 sm:flex-none">
            From
            <Input
              type="date"
              aria-label="Subscribed from"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className={cn('h-8 w-full sm:w-[10rem]', adminInputClass)}
            />
          </label>
          <label className="flex flex-1 items-center gap-1.5 text-xs text-[#062B1A]/60 sm:flex-none">
            To
            <Input
              type="date"
              aria-label="Subscribed to"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className={cn('h-8 w-full sm:w-[10rem]', adminInputClass)}
            />
          </label>

          {hasActiveFilters && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-8 text-xs text-[#062B1A]/60 hover:text-[#062B1A]"
            >
              Clear filters
            </Button>
          )}
        </div>

        {selectedIds.length > 0 && (
          <p className="text-xs text-[#062B1A]/60" data-testid="selection-summary">
            {selectedIds.length} selected
            {excludedFromMail > 0 && (
              <span className="text-[#062B1A]/45">
                {' — '}
                {excludedFromMail} cannot be emailed (unsubscribed or without consent)
              </span>
            )}
          </p>
        )}
      </TableFilterCard>

      {/* The table below stays for real screens; the cards after it are the
          same subscribers on a phone. */}
      <DesktopTableOnly>
      <Card className={tableCardClass}>
        <CardContent className="p-0">
          <div className="min-w-0 overflow-x-auto">
            <Table style={{ minWidth: TABLE_MIN_WIDTH }}>
              <TableHeader>
                <TableRow
                  className={cn('grid', tableHeaderRowClass)}
                  style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
                >
                  {COLUMNS.map((column) => (
                    <TableHead
                      key={column.id}
                      className={cn(
                        tableHeadCellClass,
                        'flex items-center',
                        column.id === 'select' && STICKY_HEAD_LEFT,
                        column.id === 'actions' && cn(STICKY_HEAD_RIGHT, 'justify-end'),
                      )}
                    >
                      {column.id === 'select' ? (
                        <Checkbox
                          aria-label="Select all on this page"
                          checked={allVisibleSelected}
                          onCheckedChange={() => setSelectedIds((current) => (
                            allVisibleSelected
                              ? current.filter((id) => !visibleIds.includes(id))
                              : [...new Set([...current, ...visibleIds])]
                          ))}
                          className="border-white/50 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-[#062B1A]"
                        />
                      ) : column.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                    <TableRow
                      key={`skeleton-${index}`}
                      className={cn('grid', tableRowClass)}
                      style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
                      data-testid="subscriber-skeleton-row"
                    >
                      {COLUMNS.map((column) => (
                        <TableCell key={column.id} className={cn(tableCellClass, ROW_PADDING)}>
                          <div className="h-4 animate-pulse rounded bg-[#062B1A]/8" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableEmptyRow colSpan={COLUMNS.length}>
                    {hasActiveFilters
                      ? 'No subscribers match these filters.'
                      : 'No newsletter subscribers yet. Opt-ins from the website appear here.'}
                  </TableEmptyRow>
                ) : (
                  items.map((subscriber) => {
                    const meta = subscriberStatusMeta(subscriber.status);
                    const active = subscriber.status === 'active';
                    return (
                      <TableRow
                        key={subscriber.id}
                        data-testid="subscriber-row"
                        className={cn('grid', tableRowClass)}
                        style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
                      >
                        <TableCell className={cn(tableCellClass, ROW_PADDING, STICKY_LEFT)}>
                          <Checkbox
                            aria-label={`Select ${subscriber.email}`}
                            checked={selectedIds.includes(subscriber.id)}
                            onCheckedChange={() => toggleRow(subscriber.id)}
                          />
                        </TableCell>
                        <TableCell className={cn(tableCellClass, ROW_PADDING, 'truncate font-medium')}>
                          {displayValue(subscriber.email)}
                        </TableCell>
                        <TableCell className={cn(tableCellClass, ROW_PADDING)}>
                          <StatusPill tone={meta.pillTone}>{meta.label}</StatusPill>
                        </TableCell>
                        <TableCell className={cn(tableCellClass, ROW_PADDING, 'text-[#062B1A]/70')}>
                          {localeLabel(subscriber.locale)}
                        </TableCell>
                        <TableCell className={cn(tableCellClass, ROW_PADDING, 'truncate text-[#062B1A]/60')}>
                          {sourcePageLabel(subscriber.source_page)}
                        </TableCell>
                        <TableCell
                          className={cn(tableCellClass, ROW_PADDING, 'truncate text-[#062B1A]/60')}
                          title={subscriber.consent_at
                            ? `Agreed ${formatDateTime(subscriber.consent_at)}`
                            : undefined}
                        >
                          {subscriber.consent_to_marketing
                            ? displayValue(subscriber.consent_text_version)
                            : 'No consent on record'}
                        </TableCell>
                        <TableCell className={cn(tableCellClass, ROW_PADDING, 'text-[#062B1A]/60')}>
                          {formatDate(subscriber.subscribed_at || subscriber.created_at)}
                        </TableCell>
                        <TableCell
                          className={cn(tableCellClass, ROW_PADDING, STICKY_RIGHT, 'flex justify-end')}
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={!canEdit || busy}
                            data-testid={active ? 'unsubscribe-action' : 'reactivate-action'}
                            onClick={() => setStatusTarget({
                              subscriber,
                              nextStatus: active ? 'unsubscribed' : 'active',
                            })}
                            className={cn(
                              'h-7 px-2 text-xs',
                              active
                                ? 'text-red-700 hover:bg-red-50'
                                : 'text-[#062B1A]/70 hover:text-[#062B1A]',
                            )}
                          >
                            {active ? 'Unsubscribe' : 'Reactivate'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      </DesktopTableOnly>

      <MobileCardList data-testid="subscriber-card-list">
        {loading ? (
          Array.from({ length: SKELETON_ROWS }).map((_, index) => (
            <div
              key={`card-skeleton-${index}`}
              data-testid="subscriber-skeleton-card"
              className="h-24 animate-pulse rounded-xl border border-[rgba(6,43,26,0.08)] bg-white"
            />
          ))
        ) : items.length === 0 ? (
          <MobileCardNotice>
            {hasActiveFilters
              ? 'No subscribers match these filters.'
              : 'No newsletter subscribers yet. Opt-ins from the website appear here.'}
          </MobileCardNotice>
        ) : (
          items.map((subscriber) => {
            const meta = subscriberStatusMeta(subscriber.status);
            const active = subscriber.status === 'active';
            return (
              <MobileCard key={subscriber.id} data-testid="subscriber-card">
                <MobileCardHeader
                  title={displayValue(subscriber.email)}
                  meta={sourcePageLabel(subscriber.source_page)}
                  badge={<StatusPill tone={meta.pillTone}>{meta.label}</StatusPill>}
                />
                <MobileCardFields>
                  <MobileCardField label="Language">
                    {localeLabel(subscriber.locale)}
                  </MobileCardField>
                  <MobileCardField label="Consent">
                    {subscriber.consent_to_marketing
                      ? displayValue(subscriber.consent_text_version)
                      : 'No consent on record'}
                  </MobileCardField>
                  <MobileCardField label="Subscribed">
                    {formatDate(subscriber.subscribed_at || subscriber.created_at)}
                  </MobileCardField>
                </MobileCardFields>
                {/* This list has no detail screen to send anyone to, so the
                    one action a card needs stays on the card. */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canEdit || busy}
                  data-testid={active ? 'unsubscribe-action-card' : 'reactivate-action-card'}
                  onClick={() => setStatusTarget({
                    subscriber,
                    nextStatus: active ? 'unsubscribed' : 'active',
                  })}
                  className={cn(
                    'mt-3 h-9 w-full text-xs',
                    active
                      ? 'border-red-200 text-red-700 hover:bg-red-50'
                      : 'border-[rgba(6,43,26,0.16)] text-[#062B1A]',
                  )}
                >
                  {active ? 'Unsubscribe' : 'Reactivate'}
                </Button>
              </MobileCard>
            );
          })
        )}
      </MobileCardList>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-[#062B1A]/55">Page {page + 1} of {pageCount}</p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page === 0 || loading}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
            >
              <ChevronLeft className="mr-1 size-4" /> Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page + 1 >= pageCount || loading}
              onClick={() => setPage((current) => current + 1)}
              className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
            >
              Next <ChevronRight className="ml-1 size-4" />
            </Button>
          </div>
        </div>
      )}

      <SubscriberStatusDialog
        open={!!statusTarget}
        subscriber={statusTarget?.subscriber}
        nextStatus={statusTarget?.nextStatus}
        busy={busy}
        onCancel={() => setStatusTarget(null)}
        onConfirm={runStatusChange}
      />
    </WebsiteEngagementShell>
  );
}
